import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { AccountCashesBalancesReportFindInterface } from '@zaoblako/shared/modules/dashboard/dtos/acb/account-cashes-balances-report-find.dto';
import type { AvailableWidgetTreeInterface } from '@zaoblako/shared/modules/dashboard/dtos/available.widget.tree.dto';
import type { DashboardTemplateFindInterface } from '@zaoblako/shared/modules/dashboard/dtos/dashboard-template/dashboard-template-find.dto';
import type { DashboardTemplateShareWithInterface } from '@zaoblako/shared/modules/dashboard/dtos/dashboard-template/dashboard-template-share-with.dto';
import type { EventCalendarReportQueryInterface } from '@zaoblako/shared/modules/dashboard/dtos/event-calendar/event-calendar-report-query.dto';
import type { HelpdeskLastTasksWidgetResultFindInterface } from '@zaoblako/shared/modules/dashboard/dtos/helpdesk/last-tasks/helpdesk-last-tasks-widget-result-find.dto';
import type { HelpdeskNewTasksWidgetResultFindInterface } from '@zaoblako/shared/modules/dashboard/dtos/helpdesk/new-tasks/helpdesk-new-tasks-widget-result-find.dto';
import type { HelpdeskOpenedTasksWidgetResultFindInterface } from '@zaoblako/shared/modules/dashboard/dtos/helpdesk/open-tasks/helpdesk-opened-tasks-widget-result-find.dto';
import type { HelpdeskOpenedTasksWidgetQueryInterface } from '@zaoblako/shared/modules/dashboard/dtos/helpdesk/open-tasks/helpdesk-opened-tasks-widget-query.dto';
import type { HelpdeskStatsWidgetQueryInterface } from '@zaoblako/shared/modules/dashboard/dtos/helpdesk/stats/helpdesk-stats-widget-query.dto';
import type { HelpdeskStatsWidgetResultInterface } from '@zaoblako/shared/modules/dashboard/dtos/helpdesk/stats/helpdesk-stats-widget-result.dto';
import { OverspendingWidgetItemInterface } from '@zaoblako/shared/modules/dashboard/dtos/overspending-widget/overspending-widget-item.dto';
import { OverspendingWidgetQueryInterface } from '@zaoblako/shared/modules/dashboard/dtos/overspending-widget/overspending-widget-query.dto';
import { ProfitAndLossDirectionReportInterface } from '@zaoblako/shared/modules/dashboard/dtos/pnl/profit-and-loss-direction-report.dto';
import { ProfitAndLossReportInterface } from '@zaoblako/shared/modules/dashboard/dtos/pnl/profit-and-loss-report.dto';
import type { ProjectActionsWidgetQueryInterface } from '@zaoblako/shared/modules/dashboard/dtos/project-actions/project-actions-widget-query.dto';
import type { ProjectActionsWidgetResultInterface } from '@zaoblako/shared/modules/dashboard/dtos/project-actions/project-actions-widget-result.dto';
import type { SavedFilterReportQueryInterface } from '@zaoblako/shared/modules/dashboard/dtos/saved-filter-report-query.dto';
import type { DashboardSettingsItemFindInterface } from '@zaoblako/shared/modules/dashboard/dtos/settings/dashboard.settings.item.find.dto';
import type { UnallocatedDocumentsFindInterface } from '@zaoblako/shared/modules/dashboard/dtos/unallocated-documents/unallocated-documents-find.dto';
import type { UserBoardTabInterface } from '@zaoblako/shared/modules/dashboard/dtos/user-board-tab.dto';
import type { VatAccountsReportInterface } from '@zaoblako/shared/modules/dashboard/dtos/vat-accounts-report/vat-accounts-report.dto';
import type { VatAccountsReportQueryInterface } from '@zaoblako/shared/modules/dashboard/dtos/vat-accounts-report/vat-accounts-report-query.dto';
import type { WidgetBaseQueryInterface } from '@zaoblako/shared/modules/dashboard/dtos/widget/widget-base-query.dto';
import { DashboardWidgetTypeEnum } from '@zaoblako/shared/modules/dashboard/enums/dashboard.widget.type.enum';
import { BalanceSourceEnum } from '@zaoblako/shared/modules/finance/enums/balance-source.enum';
import type { CreditDebitReportPageInterface } from '@zaoblako/shared/modules/debts/dtos/credit-debit-report/credit-debit-report-page.dto';
import type { CreditDebitReportQueryInterface } from '@zaoblako/shared/modules/debts/dtos/credit-debit-report/credit-debit-report-query.dto';
import type { ReportDescriptorInterface } from '@zaoblako/shared/common/dtos/report/report-descriptor.dto';
import { AbcAnalysisPageInterface } from '@zaoblako/shared/modules/document/dtos/abc-analysis/abc-analysis-page.dto';
import { AbcAnalysisWidgetQueryInterface } from '@zaoblako/shared/modules/document/dtos/abc-analysis/abc-analysis-widget-query.dto';
import type { CashFlowAnalysisFindInterface } from '@zaoblako/shared/modules/document/dtos/cash-flow-analysis/cash-flow-analysis-find.dto';
import type { CashFlowAnalysisQueryInterface } from '@zaoblako/shared/modules/document/dtos/cash-flow-analysis/cash-flow-analysis-query.dto';
import { CreditDebitReportTypeEnum } from '@zaoblako/shared/modules/document/enums/credit.debit.report.type.enum';
import type { BalanceSheetWidgetQueryInterface } from '@zaoblako/shared/modules/finance/dtos/balance-sheet-widget/balance-sheet-widget-query.dto';
import type { BalanceSheetWidgetInterface } from '@zaoblako/shared/modules/finance/dtos/balance-sheet-widget/balance-sheet-widget.dto';
import type { HelpdeskLastTasksQueryInterface } from '@zaoblako/shared/modules/helpdesk/dto/widgets/helpdesk.last.tasks.query.dto';
import type { EventCalendarWidgetResultFindInterface } from '@zaoblako/shared/modules/report/dtos/event-calendar/event-calendar-widget-result-find.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { map, Observable } from 'rxjs';
import { DashboardAccountsDebt } from '../../../modules/dashboard/models/dashboard-accounts-debt.interface';
import { DashboardUtilsService } from './dashboard-utils.service';

@Injectable()
export class DashboardService extends BaseService {
  protected baseUrl = '/basis/board/dashboard';

  /** "BASIS" module widgets' base URL */
  private basisWidgetUrl = '/basis/widget';

  /** "DEBTS" module widgets' base URL */
  private debtsWidgetUrl = '/debts/report';

  private widgetInfoUrl = '/dashboard';

  /** "HELPDESK" module widgets' base URL */
  private helpdeskWidgetsBaseUrl = '/helpdesk/widget';

  /** Base URL of widgets from "PLAN_FACT" section ("FINANCE" module) */
  private planFactWidgetUrl = '/finance/plan-fact/widget';

  /** "DOCUMENT" module widgets' base URL */
  private documentWidgetsBaseUrl = '/finance/document';

  public constructor(
    private readonly dashboardUtilsService: DashboardUtilsService,
    private readonly directionService: DirectionService
  ) {
    super();
  }

  public getWidgetsList(): Observable<AvailableWidgetTreeInterface[]> {
    return this.http.get<AvailableWidgetTreeInterface[]>('/basis/board/available-widget-types');
  }

  public getUserBoard(): Observable<UserBoardTabInterface[]> {
    return this.http.get<UserBoardTabInterface[]>('/basis/board/user');
  }

  public setUserBoard(body: UserBoardTabInterface[]): Observable<UserBoardTabInterface[]> {
    return this.http.post<UserBoardTabInterface[]>('/basis/board/user', body);
  }

  public toTemplate(
    dashboardId: number,
    body: DashboardTemplateShareWithInterface
  ): Observable<DashboardTemplateFindInterface> {
    return this.http.post<DashboardTemplateFindInterface>(`${this.baseUrl}/${dashboardId}/to-template`, body);
  }

  public selectDashboard(dashboardId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${dashboardId}/select`, {});
  }

  public savedFilterReport(queryParams: SavedFilterReportQueryInterface): Observable<any> {
    return this.http.get<any>(`${this.basisWidgetUrl}/saved-filter-report`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getBalances(
    widget: DashboardSettingsItemFindInterface,
    forceUpdate = false
  ): Observable<AccountCashesBalancesReportFindInterface> {
    let params = this.widgetDataToHttpParams(widget);
    if (forceUpdate) {
      params = params.set('forceUpdate', true);
    }
    return this.http.get<AccountCashesBalancesReportFindInterface>(`${this.basisWidgetUrl}/accounts-and-cashes`, {
      params: params
    });
  }

  public getProfitAndLoss(widget: DashboardSettingsItemFindInterface): Observable<ProfitAndLossReportInterface> {
    return this.http.get<ProfitAndLossReportInterface>(`${this.planFactWidgetUrl}/profit-and-loss`, {
      params: this.widgetDataToHttpParams(widget)
    });
  }

  public getAccountsWidget(
    widget: DashboardSettingsItemFindInterface,
    forceUpdate = false
  ): Observable<DashboardAccountsDebt> {
    let params = this.widgetDataToHttpParams(widget);

    if (
      [
        DashboardWidgetTypeEnum.DEBTORS,
        DashboardWidgetTypeEnum.ACCOUNTS_RECEIVABLE,
        DashboardWidgetTypeEnum.ACCOUNTS_PAYABLE
      ].includes(widget.widgetType) &&
      forceUpdate
    ) {
      params = params.set('forceUpdate', true);
    }

    let request: Observable<CreditDebitReportPageInterface>;
    if (widget.widgetType === DashboardWidgetTypeEnum.ACCOUNTS_RECEIVABLE) {
      request = this.getAccountsReceivable(params, false);
    } else if (widget.widgetType === DashboardWidgetTypeEnum.ACCOUNTS_PAYABLE) {
      request = this.getAccountsPayable(params, false);
    } else if (widget.widgetType === DashboardWidgetTypeEnum.DEBTORS) {
      request = this.getDebtors(params, false);
    }

    return request.pipe(
      map((data) => ({
        ...data,
        items: this.dashboardUtilsService.getAccountsDebtWidgetData(data.items)
      }))
    );
  }

  public getAccountsReceivable(
    queryParams?: CreditDebitReportQueryInterface | HttpParams,
    toHttpParams = true
  ): Observable<CreditDebitReportPageInterface> {
    return this.http.get<CreditDebitReportPageInterface>(`${this.debtsWidgetUrl}/${CreditDebitReportTypeEnum.DEBIT}`, {
      params: (toHttpParams ? this.objectToHttpParams(queryParams) : queryParams) as any
    });
  }

  public getAccountsPayable(
    queryParams?: CreditDebitReportQueryInterface | HttpParams,
    toHttpParams = true
  ): Observable<CreditDebitReportPageInterface> {
    return this.http.get<CreditDebitReportPageInterface>(`${this.debtsWidgetUrl}/${CreditDebitReportTypeEnum.CREDIT}`, {
      params: (toHttpParams ? this.objectToHttpParams(queryParams) : queryParams) as any
    });
  }

  public getDebtors(
    queryParams?: CreditDebitReportQueryInterface | HttpParams,
    toHttpParams = true
  ): Observable<CreditDebitReportPageInterface> {
    return this.http.get<CreditDebitReportPageInterface>(
      `${this.debtsWidgetUrl}/${CreditDebitReportTypeEnum.DEBTORS}`,
      {
        params: (toHttpParams ? this.objectToHttpParams(queryParams) : queryParams) as any
      }
    );
  }

  public getDebtorsRegistryDescriptor(): Observable<ReportDescriptorInterface> {
    return this.http.get<ReportDescriptorInterface>(`${this.debtsWidgetUrl}/debtors-registry-descriptor`);
  }

  public getDebitRegistryDescriptor(): Observable<ReportDescriptorInterface> {
    return this.http.get<ReportDescriptorInterface>(`${this.debtsWidgetUrl}/debit-registry-descriptor`);
  }

  public getCreditRegistryDescriptor(): Observable<ReportDescriptorInterface> {
    return this.http.get<ReportDescriptorInterface>(`${this.debtsWidgetUrl}/credit-registry-descriptor`);
  }

  public getUnallocatedDocuments(
    widget: DashboardSettingsItemFindInterface
  ): Observable<UnallocatedDocumentsFindInterface> {
    return this.http.get<UnallocatedDocumentsFindInterface>(`${this.widgetInfoUrl}/unallocated-documents`, {
      params: this.widgetDataToHttpParams(widget)
    });
  }

  public getDirectionBudget(
    widget: DashboardSettingsItemFindInterface
  ): Observable<ProfitAndLossDirectionReportInterface[]> {
    return this.http.get<ProfitAndLossDirectionReportInterface[]>(`${this.planFactWidgetUrl}/budget-per-direction`, {
      params: this.widgetDataToHttpParams(widget)
    });
  }

  public getEventCalendar(
    queryParams: EventCalendarReportQueryInterface
  ): Observable<EventCalendarWidgetResultFindInterface> {
    return this.http.get<EventCalendarWidgetResultFindInterface>('/project/report/event-calendar/widget', {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getVatAccounts(queryParams: VatAccountsReportQueryInterface): Observable<VatAccountsReportInterface> {
    return this.http.get<VatAccountsReportInterface>(`${this.basisWidgetUrl}/vat-accounts`, {
      params: queryParams as any
    });
  }

  public getProjectActions(
    queryParams: ProjectActionsWidgetQueryInterface
  ): Observable<ProjectActionsWidgetResultInterface> {
    return this.http.get<ProjectActionsWidgetResultInterface>('/project/project-actions', {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getHelpdeskOpenedTasks(
    queryParams: HelpdeskOpenedTasksWidgetQueryInterface
  ): Observable<HelpdeskOpenedTasksWidgetResultFindInterface> {
    return this.http.get<HelpdeskOpenedTasksWidgetResultFindInterface>(`${this.helpdeskWidgetsBaseUrl}/opened-tasks`, {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getHelpdeskNewTasks(
    queryParams?: HelpdeskOpenedTasksWidgetQueryInterface
  ): Observable<HelpdeskNewTasksWidgetResultFindInterface> {
    return this.http.get<HelpdeskNewTasksWidgetResultFindInterface>(`${this.helpdeskWidgetsBaseUrl}/new-tasks`, {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getHelpdeskStats(
    queryParams: HelpdeskStatsWidgetQueryInterface
  ): Observable<HelpdeskStatsWidgetResultInterface> {
    return this.http.get<HelpdeskStatsWidgetResultInterface>(`${this.helpdeskWidgetsBaseUrl}/stats`, {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getHelpDeskLastTasks(
    queryParams: HelpdeskLastTasksQueryInterface
  ): Observable<HelpdeskLastTasksWidgetResultFindInterface> {
    return this.http.get<HelpdeskLastTasksWidgetResultFindInterface>(`${this.helpdeskWidgetsBaseUrl}/last-tasks`, {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getCashFlow(queryParams: CashFlowAnalysisQueryInterface): Observable<CashFlowAnalysisFindInterface> {
    return this.http.get<CashFlowAnalysisFindInterface>(`${this.documentWidgetsBaseUrl}/cash-flow`, {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getAbcAnalysis(queryParams: AbcAnalysisWidgetQueryInterface): Observable<AbcAnalysisPageInterface> {
    return this.http.get<AbcAnalysisPageInterface>(`${this.documentWidgetsBaseUrl}/abc-analysis/widget`, {
      params: this.objectToHttpParams(queryParams as any)
    });
  }

  public getOverspending(queryParams: OverspendingWidgetQueryInterface): Observable<OverspendingWidgetItemInterface[]> {
    return this.http.get<OverspendingWidgetItemInterface[]>(`${this.planFactWidgetUrl}/overspending`, {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public getBalanceSheet(queryParams: BalanceSheetWidgetQueryInterface): Observable<BalanceSheetWidgetInterface> {
    return this.http.get<BalanceSheetWidgetInterface>('/finance/plan-fact/report/balance-sheet/widget', {
      params: this.objectToHttpParams(queryParams) as any
    });
  }

  public widgetDataToHttpParams(widget: DashboardSettingsItemFindInterface): HttpParams {
    const params: WidgetBaseQueryInterface = {
      directionIds: this.directionService.getRootDirections(widget.directions)?.map((direction) => direction.id),
      unitIds: widget.units?.map((unit) => unit.id),
      companyIds: widget.companies?.map((company) => company.id),
      bankDetailsIds: widget.bankDetails?.map((bankDetail) => bankDetail.id),
      method: widget.method,
      period: widget.calendarPeriod,
      shift: widget.periodShift
    };

    if (widget.widgetType === DashboardWidgetTypeEnum.DEBTORS) {
      params['onlyPositive'] = widget.onlyPositive;
    }
    if (widget.widgetType === DashboardWidgetTypeEnum.UNALLOCATED_DOCUMENTS) {
      params['methods'] = widget.methods;
      delete params.method;
    }
    if (widget.widgetType === DashboardWidgetTypeEnum.ACCOUNTS_MONEY) {
      params['sort'] = widget['sortField'];
      params['balanceSource'] = widget.balanceSource ?? BalanceSourceEnum.ONE_C;
    }
    if (widget.widgetType === DashboardWidgetTypeEnum.PROFIT_AND_LOSS) {
      params['scenarioId'] = widget.planningScenario?.id;
    }
    if (widget['withoutIntergroupTurnover']) {
      params['withoutIntergroupTurnover'] = true;
    }
    return this.objectToHttpParams(params);
  }
}
